// SPDX-License-Identifier: MIT
import { supabase } from '../auth/supabase';
import type { MagnifyResult } from '../auth/magnify';
import type { Locale } from '../i18n/detect';

// Row schema for public.projects (matches the migration
// supabase/migrations/2026-05-16T1500_create_projects.sql).
//
// RLS guarantees user_id = auth.uid() on every read/write, so the SPA
// never has to filter client-side. supabase-js attaches the user JWT
// automatically when the client was initialized with persistSession.
export interface ProjectRow {
  id: string;
  user_id: string;
  title: string;
  idea: string;
  outline: string[];
  first_steps: string[];
  stack: string | null;
  locale: Locale;
  created_at: string;
  updated_at: string;
  // Set by the forgejo-bootstrap edge fn after it creates the Forgejo repo
  // from this project's manifest. Null = never pushed ; non-null = the SPA
  // renders "✓ On Forgejo" instead of the Push button.
  forgejo_repo_url: string | null;
  // Read-only clone token (cf gitclone volet ②) — the bootstrap edge fn stamps it
  // so the clone command authenticates against the login-walled instance,
  // automatic for all users. Null = no token configured (or pre-feature row).
  forgejo_clone_token?: string | null;
  // 2026-05-17 v17 — version history. The DB trigger inserts v1 on initial
  // INSERT ; save_new_project_version() rpc bumps + INSERTS atomically on
  // each regen/enriched/discovery call. The .title/.outline/.first_steps/
  // .stack fields on this row ALWAYS mirror the current version's content
  // (single source of truth for "what to display").
  current_version_number: number;
  // 2026-05-18 v26 — persisted discovery state. discover-attachments edge
  // fn writes summary/questions/attachments_* + run_at on every run.
  // discovery_answers is written separately by the SPA when the user
  // submits the questions form (saveDiscoveryAnswers helper). All null
  // before the first discovery run on this project.
  discovery_summary: string | null;
  discovery_questions: { id: string; question: string; placeholder?: string }[] | null;
  discovery_answers: Record<string, string> | null;
  discovery_attachments_seen: string[] | null;
  discovery_attachments_skipped: { name: string; reason: string }[] | null;
  discovery_run_at: string | null;
  // 2026-05-19 Phase 3.A BYO — user-provided Supabase backend credentials
  // baked into the generated repo's .env.local by forgejo-bootstrap. All
  // three columns are null together (no backend wired = bootstrap writes
  // placeholder .env.example) OR all three set together ('byo-supabase' +
  // url + anon_key). DB constraint `projects_backend_config_completeness`
  // enforces this — never read a partial state.
  backend_kind: 'byo-supabase' | 'genesis-rest' | 'full-supabase' | null;
  backend_url: string | null;
  backend_anon_key: string | null;
  // 2026-06-09 — deploy_frontend slice. The deploy-frontend edge fn (gen-deployer
  // broker) deploys the generated repo to <handle>.genesis-platform.fr. deploy_url
  // is set once a deploy starts ; deploy_status walks building → live | failed ;
  // deploy_id is the deployer's handle for status polling. All null = never deployed.
  deploy_url: string | null;
  deploy_status: 'building' | 'live' | 'failed' | null;
  deployed_at: string | null;
  deploy_id: string | null;
  // 2026-07-10 — deploy-expiry-banner slice (Task A1). The real free-tier expiry
  // propagated from gen-deployer's ledger. NULL = never deployed OR Cloud-permanent
  // (no countdown). Written by the front-deployed hook + the deploy-frontend
  // status-poll (Part C, separate task) — this row-plumbing task only carries it
  // through. `select('*')` passthrough — no explicit per-column mapping needed.
  deploy_expires_at: string | null;
  // CP-1 — idea-quota metering gate. 'web' rows are counted by the DB trigger;
  // 'desktop_sync' rows are exempt (desktop licence = unlimited local ideas).
  origin: 'web' | 'desktop_sync';
  // DESKTOP-ONLY: the local folder where this project was delivered (scaffolded +
  // git init). Persisted in projects.json so a re-open doesn't offer to create the
  // folder again (and so the agent knows where to work). Always undefined on web
  // (no such column) — never sent to the cloud.
  local_dir?: string | null;
}

export type VersionContextKind = 'initial' | 'regen' | 'enriched' | 'discovery';

// One row in public.project_versions. Returned by listProjectVersions().
export interface ProjectVersionRow {
  id: string;
  project_id: string;
  version_number: number;
  title: string;
  outline: string[];
  first_steps: string[];
  stack: string | null;
  context_kind: VersionContextKind;
  context_snapshot: string | null;
  created_at: string;
}

// Persist a magnified result + the original idea string. The SPA passes
// the idea separately (the user typed it) since the MagnifyResult doesn't
// include it — that lets us show the original prompt in "Mes projets" so
// the user remembers what they asked for.
export async function saveProject(
  result: MagnifyResult,
  idea: string,
  locale: Locale,
  origin: 'web' | 'desktop_sync' = 'web',
): Promise<ProjectRow> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: user.id,
      title: result.title,
      idea,
      outline: result.outline,
      first_steps: result.first_steps,
      stack: result.stack ?? null,
      locale,
      origin,
    })
    .select()
    .single();

  if (error) throw error;
  return data as ProjectRow;
}

// Derive a short, human title from a freeform idea for a seed (manifest-less)
// project row. First non-empty line, trimmed, capped at 80 chars.
export function deriveSeedTitle(idea: string): string {
  const firstLine = (idea.split('\n').find((l) => l.trim().length > 0) ?? '').trim();
  if (!firstLine) return 'Mon projet';
  return firstLine.length > 80 ? firstLine.slice(0, 80).trimEnd() : firstLine;
}

// Persist a SEED project row (bootstrap-early path): no manifest yet —
// outline/first_steps are empty (the local discovery fills them via
// genesis_save_manifest). Mirrors saveProject's insert shape.
export async function saveProjectSeed(idea: string, locale: Locale): Promise<ProjectRow> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: user.id,
      title: deriveSeedTitle(idea),
      idea,
      outline: [],
      first_steps: [],
      stack: null,
      locale,
      origin: 'web' as const,
    })
    .select()
    .single();

  if (error) throw error;
  return data as ProjectRow;
}

// List the user's projects, newest first. The compound index
// (user_id, created_at DESC) covers this. RLS narrows to own rows.
export async function listProjects(): Promise<ProjectRow[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as ProjectRow[];
}

// Delete a project by id. RLS rejects if it's not the user's row.
export async function deleteProject(id: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Convert a stored ProjectRow back to a MagnifyResult so the UI can
// re-render the project-preview-card from a saved entry the same way
// it renders a fresh Magnify result.
export function projectRowToMagnifyResult(row: ProjectRow): MagnifyResult {
  return {
    title: row.title,
    outline: row.outline,
    first_steps: row.first_steps,
    stack: row.stack ?? undefined,
  };
}

// Atomic regen-save : bumps projects.current_version_number, updates the
// projects.* fields to the new content, AND inserts a row in
// public.project_versions in a single TX. Returns the new version
// number so the SPA can show "v3 / N" without a follow-up SELECT.
// Mirrors the SQL function public.save_new_project_version().
//
// contextSnapshot is the attachments_context / refinement payload that
// produced the version (audit + future "what did you ask Claude" debug).
// Server-side cap at 16K chars so a huge multimodal context doesn't
// bloat the table.
export async function saveProjectVersion(
  projectId: string,
  result: MagnifyResult,
  kind: VersionContextKind,
  contextSnapshot?: string | null,
): Promise<number> {
  const { data, error } = await supabase.rpc('save_new_project_version', {
    p_project_id: projectId,
    p_title: result.title,
    p_outline: result.outline,
    p_first_steps: result.first_steps,
    p_stack: result.stack ?? null,
    p_context_kind: kind,
    p_context_snapshot: contextSnapshot ?? null,
  });
  if (error) throw error;
  return data as number;
}

// List all versions of a project, newest first. Drives the version chip
// + prev/next nav on the card. Indexed via
// project_versions_project_version_desc_idx.
export async function listProjectVersions(
  projectId: string,
): Promise<ProjectVersionRow[]> {
  const { data, error } = await supabase
    .from('project_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('version_number', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ProjectVersionRow[];
}

// Switch the projects.* "current" view to an older version's content.
// Use case : user clicks the prev arrow to look at v2, then continues
// regenerating from there → v6 would be derived from v2's content
// (not from the latest v5). The previous versions are preserved in
// project_versions ; only the displayed/current pointer changes.
// Returns the version_number switched to (== the input).
export async function switchToProjectVersion(
  projectId: string,
  versionNumber: number,
): Promise<number> {
  const { data, error } = await supabase.rpc('switch_to_project_version', {
    p_project_id: projectId,
    p_version: versionNumber,
  });
  if (error) throw error;
  return data as number;
}

// Convert a ProjectVersionRow back to a MagnifyResult. Same shape
// transformation as projectRowToMagnifyResult.
export function projectVersionToMagnifyResult(v: ProjectVersionRow): MagnifyResult {
  return {
    title: v.title,
    outline: v.outline,
    first_steps: v.first_steps,
    stack: v.stack ?? undefined,
  };
}

// 2026-05-18 v26 — persist the user's discovery answers (Q&A textarea
// values) on the project row. Called from the discovery-panel submit
// handler before the re-magnify call. Discovery_summary + questions
// are written by the edge fn ; answers are user-input so they live in
// a separate save path. RLS-scoped to the user's own row.
// 2026-05-19 Phase 3.A — persist user-provided Supabase backend config on
// the project row. When the user fills the "Configure backend" panel in the
// project preview card, this writes the trio (kind='byo-supabase', url, anon)
// in a single PATCH so the DB CHECK constraint `projects_backend_config_
// completeness` is happy. The next forgejo-bootstrap call for this project
// then bakes the values into `.env.local` (vs `.env.example` placeholders).
//
// Rotation : calling this again with new values overwrites — the constraint
// allows transitioning between (null, null, null) and (kind, url, anon) but
// rejects partial mid-states. Pass either all-three set OR call clearProjectBackend.
export async function saveProjectBackend(
  projectId: string,
  url: string,
  anonKey: string,
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({
      backend_kind: 'byo-supabase',
      backend_url: url,
      backend_anon_key: anonKey,
    })
    .eq('id', projectId);
  if (error) throw error;
}

// Cloud-sync v2 — push a desktop-provisioned (or BYO) backend trio UP to the cloud
// row, PRESERVING the real kind. saveProjectBackend hardcodes 'byo-supabase' (it
// serves the BYO-paste UI), which would mislabel a genesis-rest / full-supabase
// backend, so the bidirectional sync needs this distinct kind-preserving write.
// The caller (run-cloud-sync) applies the fill-missing guard (push only when the
// cloud trio is empty).
export async function pushBackendConfig(
  projectId: string,
  kind: 'byo-supabase' | 'genesis-rest' | 'full-supabase',
  url: string,
  anonKey: string,
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ backend_kind: kind, backend_url: url, backend_anon_key: anonKey })
    .eq('id', projectId);
  if (error) throw error;
}

// Clear the backend config (sets the trio back to null/null/null). Used when
// the user wants to switch from BYO back to no-backend (re-bootstrap would
// then emit `.env.example` placeholders again).
export async function clearProjectBackend(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({
      backend_kind: null,
      backend_url: null,
      backend_anon_key: null,
    })
    .eq('id', projectId);
  if (error) throw error;
}

// Push ONE version row to the cloud at its EXACT version_number (idempotent via
// the sync_project_version RPC — ON CONFLICT DO NOTHING + advance current view if
// highest). Distinct from saveProjectVersion() which auto-bumps. Used by the
// desktop→cloud version sync (run-cloud-sync.pushVersionsFor).
export async function saveProjectVersionAt(
  projectId: string,
  v: ProjectVersionRow,
): Promise<void> {
  const { error } = await supabase.rpc('sync_project_version', {
    p_project_id: projectId,
    p_version_number: v.version_number,
    p_title: v.title,
    p_outline: v.outline,
    p_first_steps: v.first_steps,
    p_stack: v.stack ?? null,
    p_context_kind: v.context_kind,
    p_context_snapshot: v.context_snapshot ?? null,
  });
  if (error) throw error;
}

// Push forgejo_repo_url desktop→cloud. Caller gates on cloud being empty
// (fill-missing). RLS update-own allows the owner to update their project.
export async function pushForgejoRepoUrl(projectId: string, url: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ forgejo_repo_url: url })
    .eq('id', projectId);
  if (error) throw error;
}

// ─── project_sessions (cross-workstation build-session history, metadata only) ──
// Only session/request METADATA is stored (NO raw feed) — see session-sync.ts +
// the migration header. Owner-scoped by RLS.
export interface CloudSessionRow {
  id: string;
  project_id: string;
  started_at: string;
  requests: unknown; // jsonb [{id,prompt,kind,started_at,summary}]
}

export async function listProjectSessions(projectId: string): Promise<CloudSessionRow[]> {
  const { data, error } = await supabase
    .from('project_sessions')
    .select('id, project_id, started_at, requests')
    .eq('project_id', projectId);
  if (error) throw error;
  return (data ?? []) as CloudSessionRow[];
}

// Upsert one session (by id) — idempotent across syncs. user_id is filled by the
// DB default (auth.uid()) so we never depend on a client-side user id (a missing
// one was silently bailing the push). The insert RLS (user_id = auth.uid()) is
// satisfied by that default.
export async function upsertProjectSession(
  projectId: string,
  s: { id: string; started_at: string; requests: unknown },
): Promise<void> {
  const { error } = await supabase
    .from('project_sessions')
    .upsert(
      { id: s.id, project_id: projectId, started_at: s.started_at, requests: s.requests, updated_at: new Date().toISOString() },
      { onConflict: 'id' },
    );
  if (error) throw error;
}

export async function saveDiscoveryAnswers(
  projectId: string,
  answers: Record<string, string>,
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ discovery_answers: answers })
    .eq('id', projectId);
  if (error) throw error;
}

// Convenience predicate : true when the project already has a stored
// discovery (summary + questions) available for restore. Lets the
// SPA decide between rendering the discovery-panel directly (restore
// path) vs the "🔍 Genesis te pose des questions" CTA (fresh path).
export function hasStoredDiscovery(row: ProjectRow): boolean {
  return Boolean(row.discovery_summary && row.discovery_questions && row.discovery_questions.length > 0);
}

// ── Conversational chat (sub-project 2) ─────────────────────────────────────
// A persisted multi-turn thread per project. The `converse` edge fn does the
// inference + creates the conversation + persists both turns ; the SPA only
// READS the history here to re-hydrate the thread when a project is reopened.

export interface MessageRow {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  // assistant turns only : did this turn materially evolve the fiche, and to
  // which version. Drives the "✓ fiche mise à jour → vN" badge on the bubble.
  manifest_changed: boolean;
  version_number: number | null;
  created_at: string;
}

// List a conversation's messages oldest-first (chat render order). RLS scopes
// to the owner via the conversation→project ownership chain.
export async function listMessages(conversationId: string): Promise<MessageRow[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as MessageRow[];
}

// Re-hydrate the chat for an open project : find its (single, v1) conversation
// and load the thread. Returns null when the user has never chatted on this
// project yet (no conversation row — the edge fn creates it on the first turn).
export async function loadConversationHistory(
  projectId: string,
): Promise<{ conversationId: string; messages: MessageRow[] } | null> {
  const { data: conv, error } = await supabase
    .from('conversations')
    .select('id')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) throw error;
  if (!conv) return null;
  const conversationId = (conv as { id: string }).id;
  const messages = await listMessages(conversationId);
  return { conversationId, messages };
}
